import { containerQueries } from "@/features/containers";
import {
  useContainerMembersQuery,
  useRemoveContainerMemberMutation,
} from "@/features/containers/api/containers-hooks";
import type { Container } from "@shared/types/supabase.types";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import type { ContainerLabel } from "../container-members-dialog.types";
import { buildMembersRoster } from "../members-roster";
import type { MembersRoster } from "../members-roster";

// Same actor-type list the chat page queries with, so the room's member query
// is one shared cache entry whichever surface reads it first.
export const MEMBER_ACTOR_TYPES = ["ai", "user", "invited_user"] as const;

/** The members-dialog label for a container type, or null for types without one. */
export function containerLabelFor(
  containerType: Container["container_type"],
): ContainerLabel | null {
  if (containerType === "room") return "chat";
  if (containerType === "message_room") return "message";
  if (containerType === "document") return "document";
  return null;
}

export type ManageMembersData = {
  /** The container row; undefined while it loads. Its `is_shared` is the source of truth. */
  container: Container | undefined;
  containerLabel: ContainerLabel | null;
  /** The container row could not be loaded (network, or no longer accessible). */
  isError: boolean;
  roster: MembersRoster;
  onRemoveMember: (containerMemberId: string) => Promise<void>;
};

/**
 * CR-177: the one data source for managing a chat, message room or document's
 * members. The header avatar group and the sidebar row menu both read it, so
 * the dialog cannot show different sharing state or members depending on where
 * it was opened from.
 */
export function useManageMembersData(containerId: string, projectId: string): ManageMembersData {
  const { data: container, isError } = useQuery({
    ...containerQueries.byId(containerId),
    enabled: !!containerId,
  });
  const { data: containerMembers } = useContainerMembersQuery(containerId, MEMBER_ACTOR_TYPES);
  const { data: projectMembers } = useContainerMembersQuery(projectId, MEMBER_ACTOR_TYPES);
  const { mutateAsync: removeMember } = useRemoveContainerMemberMutation(containerId);

  const roster = useMemo(
    () => buildMembersRoster(containerMembers, projectMembers),
    [containerMembers, projectMembers],
  );

  const onRemoveMember = useCallback(
    async (containerMemberId: string) => {
      await removeMember(containerMemberId);
    },
    [removeMember],
  );

  return {
    container,
    containerLabel: container ? containerLabelFor(container.container_type) : null,
    isError,
    roster,
    onRemoveMember,
  };
}
